// Revealing (v8.363.9): the audience a handout gets, and the vox line that announces it. Pure.
import type { Handout } from './types';
import { TO_ALL, TO_PLAYERS, type ChatMessage, type ChatChannel } from '../chat/types';

export type RevealTarget =
  | { kind: 'table' }
  | { kind: 'players' }
  | { kind: 'channel'; id: string }
  | { kind: 'people'; uids: string[] }
  | { kind: 'draft' };

/** The audience for a target. A group reveal snapshots its members now - the dialog says so. */
export function audienceForReveal(target: RevealTarget, channels: readonly ChatChannel[]): string[] {
  switch (target.kind) {
    case 'table':
      return [TO_ALL];
    case 'players':
      return [TO_PLAYERS];
    case 'channel': {
      const ch = channels.find((c) => c.id === target.id);
      return ch ? [...new Set(ch.members)] : [];
    }
    case 'people':
      return [...new Set(target.uids)];
    case 'draft':
      return [];
  }
}

export function revealPatch(
  target: RevealTarget,
  channels: readonly ChatChannel[],
  now: number,
): { audience: string[]; revealedAt: number; updatedAt: number; presentedAt?: number } {
  const audience = audienceForReveal(target, channels);
  const patch = { audience, revealedAt: audience.length ? now : 0, updatedAt: now };
  // A reveal taken back ends the presentation with it (v9.8.0); a change of audience leaves it up.
  return audience.length ? patch : { ...patch, presentedAt: 0 };
}

/**
 * Where a reveal is announced: the table for everyone, the players'
 * channel for '@players', a DM when it went to one other person, and the
 * table otherwise.
 */
function revealConversation(audience: string[], myUid: string): ChatMessage['conv'] {
  if (audience.includes(TO_ALL)) return 'table';
  if (audience.includes(TO_PLAYERS)) return 'players';
  if (audience.length === 2 && audience.includes(myUid)) {
    return `dm:${audience.find((u) => u !== myUid) ?? myUid}` as ChatMessage['conv'];
  }
  return 'table';
}

/** The vox line that shows a handout to its audience: a chat message of kind `handout`. */
export function handoutChatMessage(
  h: Handout,
  audience: string[],
  me: { uid: string; seat: string; name: string },
  now: number,
  mint: () => string,
): ChatMessage {
  const conv = revealConversation(audience, me.uid);
  return {
    id: mint(),
    ts: now,
    fromUid: me.uid,
    fromSeat: me.seat,
    fromName: me.name,
    speakAs: null,
    kind: 'handout',
    text: h.title || h.kind,
    conv,
    // '@players' excludes the GM by rule, so the GM names themself to keep their own line.
    audience: audience.includes(me.uid) || audience.includes(TO_ALL) ? audience : [...audience, me.uid],
    mentions: [],
    roll: null,
    handoutId: h.id,
  };
}

/** What the audience badge on a card says. */
export function audienceLabel(audience: readonly string[], people: readonly { uid: string; name: string }[]): string {
  if (audience.length === 0) return 'Draft';
  if (audience.includes(TO_ALL)) return 'Table';
  if (audience.includes(TO_PLAYERS)) return 'Players';
  const names = audience.map((u) => people.find((p) => p.uid === u)?.name ?? '?');
  return names.length <= 2 ? names.join(', ') : `${names.length} people`;
}
